// src/contexts/UserContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import { SUBSCRIPTION_LIMITS } from "../subscriptionLimits";

const UserContext = createContext();

export function UserProvider({ children }) {
  const [userSessions, setUserSessions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("userSessions")) || {};
    } catch {
      return {};
    }
  });

  const [activeSessionToken, setActiveSessionToken] = useState(() => {
    return localStorage.getItem("activeSessionToken") || null;
  });

  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const isRenewingRef = useRef(false);

  const logoutTimeout = useRef(null);
  const warningTimeout = useRef(null);
  const countdownInterval = useRef(null);

  // 🔹 Synchronisation multi-onglet
  useEffect(() => {
    const handleStorage = e => {
      if (e.key === "activeSessionToken") setActiveSessionToken(e.newValue);
      if (e.key === "userSessions") {
        try {
          setUserSessions(JSON.parse(e.newValue) || {});
        } catch {}
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);


  const currentUser =
    activeSessionToken && userSessions[activeSessionToken]
      ? { ...userSessions[activeSessionToken].user, token: userSessions[activeSessionToken].token }
      : null;

  const getAuthHeaders = () => {
    const session = activeSessionToken && userSessions[activeSessionToken];
    return {
      Authorization: session ? `Bearer ${session.token}` : "",
      "Content-Type": "application/json",
    };
  };

// 🔹 Conversion sécurisée de expiresAt
const parseExpiresAt = (expiresAt) => {
  if (!expiresAt) return null;

  if (typeof expiresAt === "string") return new Date(expiresAt);
  if (expiresAt instanceof Date) return expiresAt;
  if (expiresAt.$date) return new Date(expiresAt.$date);

  // fallback
  return null;
};

// loginHandler
const loginHandler = async (role, emailOrUsername, password, navigate) => {
  try {
    const endpoint = role === "admin" ? "/admin/login" : "/user/login";
    const body = role === "admin"
      ? { email: emailOrUsername.trim(), password }
      : { username: emailOrUsername.trim(), password };

    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const data = await response.json();
    if (!response.ok) {
      toast.error(data.message || "Erreur de connexion");
      return null;
    }

    const expiresAt = parseExpiresAt(data.expiresAt);

    login(data.user, data.token, data.sessionToken, expiresAt);

    // console.log("🧾 LOGIN RESPONSE:", data);
    // console.log("⏱ expiresAt traité côté frontend:", expiresAt);

    navigate(role === "admin" ? "/administrator" : "/");
    return data.user;

  } catch (err) {
    console.error(err);
    toast.error("Erreur serveur");
    return null;
  }
};

// login
const login = (userData, token, sessionToken, expiresAt) => {
  if (!sessionToken) {
    toast.error("Erreur session.");
    return;
  }

  const expiresISO =
    expiresAt instanceof Date
      ? expiresAt.toISOString()
      : typeof expiresAt === "string"
      ? expiresAt
      : null;

  if (!expiresISO) {
    console.warn("⚠️ expiresAt absent, fallback 1h");
  }

  const finalExpiresAt =
    expiresISO || new Date(Date.now() + 60 * 60 * 1000).toISOString();

  const newUser = { ...userData, sessionToken };

  setUserSessions(prev => {
    const updated = {
      ...prev,
      [sessionToken]: {
        user: newUser,
        token,
        expiresAt: finalExpiresAt, // 🔥 STRING UNIQUEMENT
      },
    };
    localStorage.setItem("userSessions", JSON.stringify(updated));
    return updated;
  });

  setActiveSessionToken(sessionToken);
  localStorage.setItem("activeSessionToken", sessionToken);
};
  

  const clearSessionTimers = () => {
    if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
    if (warningTimeout.current) clearTimeout(warningTimeout.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
    setShowSessionWarning(false);
  };



  const logout = (sessionToken = activeSessionToken) => {
    clearSessionTimers();

    setUserSessions(prev => {
      const updated = { ...prev };
      delete updated[sessionToken];
      localStorage.setItem("userSessions", JSON.stringify(updated));
      return updated;
    });

    if (sessionToken === activeSessionToken) {
      setActiveSessionToken(null);
      localStorage.removeItem("activeSessionToken");
      window.location.href = "/";
    }
  };

  const switchSession = token => {
    if (userSessions[token]) {
      setActiveSessionToken(token);
      localStorage.setItem("activeSessionToken", token);
      startSessionTimer();
    }
  };

  useEffect(() => {
    if (activeSessionToken && userSessions[activeSessionToken]) {
      startSessionTimer();
    }
  }, [activeSessionToken, userSessions]);

//   console.log("🔁 SESSION ACTIVE CHANGÉE", {
//   activeSessionToken,
//   session: userSessions[activeSessionToken],
// });




  // const isSubscriptionActive = () => {
  //   if (!currentUser || !currentUser.subscriptionDetails) return false;
  //   const sub = currentUser.subscriptionDetails;
  //   if (!sub.active || sub.suspended) return false;
  //   const now = new Date();
  //   return now >= new Date(sub.subscriptionStart) && now <= new Date(sub.subscriptionEnd);
  // };

  // const hasFeature = (key, options = {}) => {
  //   if (!currentUser) return false;
  //   if (!isSubscriptionActive()) {
  //     if (options.toastMessage !== false)
  //       toast.error("🚫 Aucun abonnement actif. Souscrivez à un plan.");
  //     return false;
  //   }

  //   const plan = currentUser.subscriptionDetails?.subscriptionType?.toLowerCase() || "gratuit";
  //   const limit = SUBSCRIPTION_LIMITS[plan]?.[key];

  //   if (limit === undefined) return false;

  //   if (typeof limit === "boolean") {
  //     if (!limit && options.toastMessage !== false)
  //       toast.error(`🔒 Fonction non disponible dans le plan ${plan.toUpperCase()}.`);
  //     return limit;
  //   }

  //   if (typeof limit === "number") {
  //     if (options.currentCount !== undefined && options.currentCount >= limit) {
  //       if (options.toastMessage !== false)
  //         toast.error(`🚫 Limite atteinte : max ${limit} ${key}.`);
  //       return false;
  //     }
  //   }

  //   return true;
  // };

const renewSession = async () => {
  if (!activeSessionToken || isRenewingRef.current) return;

  isRenewingRef.current = true;
  clearSessionTimers(); // 🛑 STOPPER LES TIMERS IMMÉDIATEMENT

  try {
    const res = await fetch("/session/renew", {
      method: "POST",
      headers: getAuthHeaders(),
    });

    const data = await res.json();
    if (!res.ok) throw new Error("Erreur renouvellement session");
    

    const newExpiresAt = new Date(data.expiresAt).toISOString();

    setUserSessions(prev => {
      const updated = {
        ...prev,
        [activeSessionToken]: {
          ...prev[activeSessionToken],
          expiresAt: newExpiresAt,
        },
      };
      localStorage.setItem("userSessions", JSON.stringify(updated));
      return updated;
    });

    setShowSessionWarning(false);
    startSessionTimer(); // 🔁 REPART AVEC LA NOUVELLE DATE

    toast.success("Session prolongée avec succès ✅");
    

  } catch (err) {
    console.error("Renew session failed:", err);
    logout();
  } finally {
    isRenewingRef.current = false;
  }
};


const startSessionTimer = () => {
  clearSessionTimers();

  const session = activeSessionToken && userSessions[activeSessionToken];
  if (!session) return;

  const expiresDate = new Date(session.expiresAt);
  if (isNaN(expiresDate.getTime())) return;

  const expiresIn = expiresDate.getTime() - Date.now();
  if (expiresIn <= 0) {
    logout();
    return;
  }

  // 🔹 Afficher warning 1 min avant expiration
  warningTimeout.current = setTimeout(() => {
    setShowSessionWarning(true);

    let seconds = Math.floor((expiresDate - Date.now()) / 1000);
    setCountdown(seconds);

   countdownInterval.current = setInterval(() => {
  if (isRenewingRef.current) return; // 🛡️ PROTECTION CLÉ

  seconds--;
  setCountdown(seconds);

  if (seconds <= 0) {
    clearInterval(countdownInterval.current);
    logout();
  }
}, 1000);
  }, Math.max(expiresIn - 60_000, 0));

  logoutTimeout.current = setTimeout(logout, expiresIn);
};

  return (
    <UserContext.Provider
      value={{
        user: currentUser,
        login,
        logout,
        loginHandler,
        renewSession,
        showSessionWarning,
        countdown,
        activeSessionToken,
        switchSession,
        allSessions: userSessions,
        getAuthHeaders,
        // hasFeature,
      }}
    >
      {children}

      {showSessionWarning && (
        <div className="session-modal-overlay">
          <div className="session-modal">
            <h3>⏰ Session expirant dans {countdown}s</h3>
            <p>Votre session est sur le point d’expirer. Voulez-vous rester connecté ?</p>
            <div className="session-modal-actions">
              <button
                onClick={() => {
                  renewSession();
                  setShowSessionWarning(false);
                }}
                className="session-btn success"
                style={{
                  backgroundColor: "#16a34a",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  marginRight: "10px",
                  cursor: "pointer",
                }}
              >
                Rester connecté
              </button>
              <button
                onClick={() => {
                  logout();
                  setShowSessionWarning(false);
                }}
                className="session-btn danger"
                style={{
                  backgroundColor: "#dc2626",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      )}
    </UserContext.Provider>
  );
}

export const useUserContext = () => useContext(UserContext);